import { bs58 } from '@coral-xyz/anchor/dist/cjs/utils/bytes';
import {
  Blockhash,
  Connection,
  Transaction,
  TransactionExpiredBlockheightExceededError,
  VersionedTransaction,
  VersionedTransactionResponse,
} from '@solana/web3.js';
import promiseRetry from 'promise-retry';
import { wait } from '../utils/wait';
import { BROADCAST_MODE } from './tx';

type TransactionSenderAndConfirmationWaiterArgs = {
  txid: string;
  connection: Connection;
  serializedTransaction: Buffer;
  blockhashWithBlockHeight: {
    blockhash: Blockhash;
    lastValidBlockHeight: number;
  };
  broadcastMode?: BROADCAST_MODE;
  jitoTipTx?: Transaction | VersionedTransaction;
  jitoProxyUrl?: string;
};

const SEND_OPTIONS = {
  maxRetries: 0,
  skipPreflight: true,
};

export type JitoResponse = {
  result: string;
};

export async function forwardToJito(
  serializedTransaction: Buffer,
  jitoTipTx: Transaction | VersionedTransaction,
  jitoProxyUrl?: string,
): Promise<JitoResponse | null> {
  try {
    const jitoResponse = await fetch(jitoProxyUrl ?? `https://mainnet.block-engine.jito.wtf/api/v1/bundles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'sendBundle',
        params: [[bs58.encode(serializedTransaction), bs58.encode(jitoTipTx.serialize())]],
      }),
    });
    const jitoResult = (await jitoResponse.json()) as JitoResponse;

    if (jitoResult.result) {
      console.log('Sent to JITO', jitoResult.result);
      return jitoResult;
    }
  } catch (error) {
    console.log('Failed to send to Jito', error);
  }

  return null;
}

export async function transactionSenderAndConfirmationWaiter({
  txid,
  connection,
  serializedTransaction,
  blockhashWithBlockHeight,
  broadcastMode,
  jitoTipTx,
  jitoProxyUrl,
}: TransactionSenderAndConfirmationWaiterArgs): Promise<{
  response: VersionedTransactionResponse | null;
  jitoResponse: JitoResponse | null;
}> {
  const controller = new AbortController();
  const abortSignal = controller.signal;

  const abortableResender = async () => {
    while (true) {
      await wait(1_750);
      if (abortSignal.aborted) return;
      try {
        await connection.sendRawTransaction(serializedTransaction, SEND_OPTIONS);
      } catch (e) {
        console.warn(`Failed to resend transaction: ${e}`);
      }
    }
  };

  let jitoResponse = null;
  try {
    if (broadcastMode === 'jito' && jitoTipTx) {
      jitoResponse = await forwardToJito(serializedTransaction, jitoTipTx, jitoProxyUrl);
    } else {
      if (broadcastMode === 'mixed' && jitoTipTx) {
        jitoResponse = await forwardToJito(serializedTransaction, jitoTipTx, jitoProxyUrl);
      }
      await connection.sendRawTransaction(serializedTransaction, SEND_OPTIONS), abortableResender();
    }

    // this would throw TransactionExpiredBlockheightExceededError
    await Promise.race([
      connection.confirmTransaction(
        {
          ...blockhashWithBlockHeight,
          signature: txid,
          abortSignal,
        },
        'confirmed',
      ),
      new Promise(async (resolve) => {
        // in case ws socket died
        while (!abortSignal.aborted) {
          await wait(2_000);
          const tx = await connection.getSignatureStatus(txid, {
            searchTransactionHistory: false,
          });
          if (tx?.value?.confirmationStatus === 'confirmed') {
            resolve(tx);
          }
        }
      }),
    ]);
  } catch (e) {
    if (e instanceof TransactionExpiredBlockheightExceededError) {
      // we consume this error and getTransaction would return null
      return { response: null, jitoResponse };
    } else {
      // invalid state from web3.js
      throw e;
    }
  } finally {
    controller.abort();
  }

  // in case rpc is not synced yet, we add some retries
  const response = await promiseRetry(
    async (retry) => {
      const response = await connection.getTransaction(txid, {
        commitment: 'confirmed',
        maxSupportedTransactionVersion: 0,
      });
      if (!response) {
        retry(response);
      }
      return response;
    },
    {
      retries: 5,
      minTimeout: 1e3,
    },
  );

  return { response, jitoResponse };
}
